import React, { useEffect, useMemo, useState } from "react";
import { MultiDropdownV2, CounterInputV2 } from "../../FormControlsV2";
import axios from "../../../services/axios";
import {
  IOptionProps,
  IOptionsProps,
} from "../../../@types/reactSelect/IOption";
import { MessageBalloon } from "../../MessageBalloon/Index";
import "./style.css";
import { capitalize } from "../../../utils/strings/capitalize";
import { useTaskCreation } from "../../../hooks/useTaskCreation";
import { sortBy } from "../../../utils/arrays/sortBy";
import { useTranslation } from "react-i18next";
import { getLocalizedApiName, LocalizedApiName } from "../../../utils/i18n/getLocalizedApiName";

interface FertilizationProps { }

interface SelectedProductProps extends IOptionProps {
  weight?: number;
}

type SelectedProductsProps = Array<SelectedProductProps>;

type FertilizationProduct = {
  id: number;
  name: LocalizedApiName;
};

export const Fertilization: React.FC<FertilizationProps> = () => {
  const { t, i18n } = useTranslation();
  const {
    payload,
    setPayload,
    setAllowTaskCreation,
    responsibleId,
    microAreaId,
  } = useTaskCreation();

  const [products, setProducts] = useState<FertilizationProduct[]>([]);
  const [selectedProducts, setSelectedProducts] =
    useState<SelectedProductsProps>([]);
  const [showProductsCounter, setShowProductsCounter] =
    useState<boolean>(false);

  const productOptions = useMemo<IOptionsProps>(
    () => products
      .map((product) => ({
        label: nameProduct(getLocalizedApiName(product.name, i18n.language)),
        value: product.id,
      }))
      .sort(sortBy("label")),
    [products, i18n.language]
  );

  useEffect(() => {
    axios.get("tasks/fertilization/products")
      .then((response) => {
        if (response.data) setProducts(response.data);
      })
      .catch((error) => console.log(error));
  }, []);

  function nameProduct(text: string) {
    var resultText = text.toLowerCase().replace(/\(([^)]+)\)/, (match, content) => {
      return '(' + content.toUpperCase() + ')';
    });

    return resultText;
  }

  useEffect(() => {
    setSelectedProducts((current) => current.map((selected) => {
      const localized = productOptions.find((option) => option.value === selected.value);
      return localized ? { ...localized, weight: selected.weight } : selected;
    }));
  }, [productOptions]);

  const handleProductsSelection = (opts: IOptionProps[]) => {
    const next = opts.map((prod) => {
      const prev = selectedProducts.find((s) => s.value === prod.value);
      return prev ? { ...prod, weight: prev.weight } : { ...prod, weight: undefined };
    });
    setSelectedProducts(next);
  };

  const handleProductWeight = (weight: number, index: number) => {
    const currProducts = selectedProducts.slice();
    currProducts[index] = { ...currProducts[index], weight };
    setSelectedProducts(currProducts);
  };

  useEffect(() => {
    if (selectedProducts.length === 0) setShowProductsCounter(false);
    else setShowProductsCounter(true);

    if (
      selectedProducts.length === 0 ||
      selectedProducts.some((prod) => typeof prod.weight === "undefined")
    )
      return setAllowTaskCreation(false);

    const noZeroWeight = !selectedProducts.some((prod) => prod.weight === 0);

    setAllowTaskCreation(noZeroWeight);
    if (noZeroWeight) {
      const products = selectedProducts.map((product) => ({
        product_id: product.value,
        weight_per_ha: product.weight,
      }));

      setPayload({
        ...payload,
        changes: [
          {
            assigned_user_id: responsibleId,
            micro_area_id: microAreaId,
            products: products,
          },
        ],
      });
    }
  }, [selectedProducts]);

  return (
    <>
      <MultiDropdownV2
        name="fertilization-products"
        placeholder={t("taskCreation.fields.selectProducts")}
        options={productOptions ?? []}
        value={selectedProducts}
        menuMount="overlay"
        onChange={(opts) => handleProductsSelection(opts)}
        label={t("taskCreation.fields.products")}
      />
      {showProductsCounter &&
        selectedProducts.map((product, index) => {
          return (
            <React.Fragment key={String(product.value)}>
              <div className="fertilization-counter-container">
                <div className="balloon-container">
                  <span className="product-label">
                    {capitalize(nameProduct(product.label))}
                  </span>
                  <MessageBalloon
                    text={t("taskCreation.help.fertilizerQuantity")}
                    pointed
                  />
                </div>
                <CounterInputV2
                  name={`fertilization-kg-${product.value}`}
                  suffix="kg/ha"
                  value={product.weight ?? 0}
                  min={0}
                  max={99999999}
                  onChange={(v) => handleProductWeight(v, index)}
                />
              </div>
            </React.Fragment>
          );
        })}
    </>
  );
};
